import { FieldType } from "../../type-system/FieldType";
import { shopifyCurrencyCodeEnumOptions } from "./constants";
import {
  ShopifyCustomerKey,
  ShopifyCustomerPaymentMethodKey,
  ShopifyOrderKey,
  ShopifyOrderToSubscriptionContractKey,
  ShopifyShopKey,
  ShopifySubscriptionBillingAttemptKey,
  ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
  ShopifySubscriptionContractKey,
  ShopifySubscriptionContractToConcatenatedLinesKey,
  ShopifySubscriptionContractToCustomerKey,
  ShopifySubscriptionContractToCustomerPaymentMethodKey,
  ShopifySubscriptionContractToShopKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionLineToSubscriptionContractKey,
  ShopifySubscriptionLineToSubscriptionOriginContractKey,
  ShopifySubscriptionManualDiscountKey,
  ShopifySubscriptionManualDiscountToSubscriptionContractKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySubscriptionContractKey,
  "2022-01": {
    name: "Shopify Subscription Contract",
    graphQLTypename: "SubscriptionContract",
    fields: [
      {
        name: "App",
        fieldType: FieldType.JSON, // TODO: Convert to BelongsTo once App is modelled
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "app", graphQLSelection: "id" } },
      },
      {
        name: "App Admin URL",
        fieldType: FieldType.URL,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "appAdminUrl",
            shopifyFieldKey: "__gadget_graphql_subscription_contract_app_admin_url",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Billing Attempts",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "billingAttempts" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionBillingAttemptKey,
          inverseFieldKey: ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
        },
      },
      {
        name: "Billing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            graphQLSelection: `
        anchors {
          day
          month
          type
        }
        interval
        intervalCount
        maxCycles
        minCycles
        `,
            shopifyFieldKey: "billing_policy",
          },
        },
      },
      {
        name: "Currency Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "currencyCode", shopifyFieldKey: "currency_code", isShopifyEnum: true },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: shopifyCurrencyCodeEnumOptions,
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "customer", graphQLSelection: "id", shopifyFieldKey: "customer_id" },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Customer Payment Method",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToCustomerPaymentMethodKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_customer_payment_method",
            graphQLFieldKey: "customerPaymentMethod",
            graphQLSelection: "id",
            graphQLOnlyField: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerPaymentMethodKey,
        },
      },
      {
        name: "Delivery Method",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_delivery_method",
            graphQLFieldKey: "deliveryMethod",
            graphQLSelection: `...on SubscriptionDeliveryMethodShipping {
          address {
            address1
            address2
            city
            company
            country
            countryCode
            firstName
            lastName
            name
            phone
            province
            provinceCode
            zip
          }
          shippingOption {
            carrierService {
              id
            }
            code
            description
            presentmentTitle
            title
          }
        }`,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Delivery Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            graphQLSelection: `anchors {
          day
          month
          type
        }
        interval
        intervalCount`,
            shopifyFieldKey: "delivery_policy",
          },
        },
      },
      {
        name: "Delivery Price",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_delivery_price",
            graphQLFieldKey: "deliveryPrice",
            graphQLSelection: `amount
currencyCode`,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Discounts",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "discounts" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionManualDiscountKey,
          inverseFieldKey: ShopifySubscriptionManualDiscountToSubscriptionContractKey,
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Last Payment Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_last_payment_status",
            graphQLFieldKey: "lastPaymentStatus",
            graphQLOnlyField: true,
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "FAILED" }, { name: "SUCCEEDED" }],
        },
      },
      {
        name: "Lines",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lines" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionLineKey,
          inverseFieldKey: ShopifySubscriptionLineToSubscriptionContractKey,
        },
      },
      {
        name: "Next Billing Date",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_next_billing_date",
            graphQLFieldKey: "nextBillingDate",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_note",
            graphQLFieldKey: "note",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orders" } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToSubscriptionContractKey,
        },
      },
      {
        name: "Origin Order",
        fieldType: FieldType.BelongsTo,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_origin_order",
            graphQLFieldKey: "originOrder",
            graphQLOnlyField: true,
            graphQLSelection: "id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_shopify_created_at",
            graphQLFieldKey: "createdAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_shopify_updated_at",
            graphQLFieldKey: "updatedAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "status",
            shopifyFieldKey: "__gadget_graphql_subscription_contract_status",
            graphQLOnlyField: true,
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "ACTIVE" }, { name: "CANCELLED" }, { name: "EXPIRED" }, { name: "FAILED" }, { name: "PAUSED" }],
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "subscriptionContracts",
      filters: {
        updatedAt: { sortKey: "UPDATED_AT" },
        createdAt: { sortKey: "CREATED_AT" },
      },
      sortableBy: {
        updatedAt: { sortKey: "UPDATED_AT", reverse: true },
        createdAt: { sortKey: "CREATED_AT", reverse: true },
        id: { sortKey: "ID", reverse: false },
        status: { sortKey: "STATUS", reverse: false },
      },
    },
  },
  "2024-01": {
    fields: [
      {
        name: "Revision Id",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "revisionId", shopifyFieldKey: "revision_id" } },
      },
    ],
  },
  "2024-07": {
    fields: [
      {
        name: "Delivery Method",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_delivery_method",
            graphQLFieldKey: "deliveryMethod",
            graphQLSelection: `...on SubscriptionDeliveryMethodShipping {
          address {
            address1
            address2
            city
            company
            country
            countryCode
            firstName
            lastName
            name
            phone
            province
            provinceCode
            zip
          }
          shippingOption {
            carrierService {
              id
            }
            code
            description
            presentmentTitle
            title
          }
        }`,
            graphQLOnlyField: true,
            requiredScopes: ["read_orders"],
          },
        },
      },
      {
        name: "Billing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            graphQLSelection: `
        anchors {
          day
          month
          type
        }
        interval
        intervalCount
        maxCycles
        minCycles
        `,
            shopifyFieldKey: "billing_policy",
            requiredScopes: ["read_products"],
          },
        },
      },
      {
        name: "Delivery Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            graphQLSelection: `anchors {
          day
          month
          type
        }
        interval
        intervalCount`,
            shopifyFieldKey: "delivery_policy",
            requiredScopes: ["read_products"],
          },
        },
      },
    ],
  },
  "2024-10": {
    fields: [
      {
        name: "Last Billing Attempt Error Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_last_billing_attempt_error_type",
            graphQLFieldKey: "lastBillingAttemptErrorType",
            graphQLOnlyField: true,
            isShopifyEnum: true,
          },
        },
        configuration: {
          allowMultiple: false,
          allowOther: true,
          options: [{ name: "CUSTOMER_ERROR" }, { name: "INVENTORY_ERROR" }, { name: "OTHER" }, { name: "PAYMENT_ERROR" }],
        },
      },
    ],
  },
  "2025-01": {
    fields: [
      {
        name: "Concatenated Lines",
        fieldType: FieldType.HasMany,
        key: ShopifySubscriptionContractToConcatenatedLinesKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionLineKey,
          inverseFieldKey: ShopifySubscriptionLineToSubscriptionOriginContractKey,
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
          },
        },
      },
      {
        name: "Billing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            graphQLSelection: `
        anchors {
          day
          month
          type
        }
        interval
        intervalCount
        maxCycles
        minCycles
        `,
            shopifyFieldKey: "billing_policy",
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
            requiredScopes: ["read_products"],
          },
        },
      },
      {
        name: "Delivery Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            graphQLSelection: `anchors {
          day
          month
          type
        }
        interval
        intervalCount`,
            shopifyFieldKey: "delivery_policy",
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
            requiredScopes: ["read_products"],
          },
        },
      },
      {
        name: "Revision Id",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "revisionId",
            shopifyFieldKey: "revision_id",
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
          },
        },
      },
      {
        name: "Currency Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "currencyCode",
            shopifyFieldKey: "currency_code",
            isShopifyEnum: true,
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: shopifyCurrencyCodeEnumOptions,
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "customer",
            graphQLSelection: "id",
            shopifyFieldKey: "customer_id",
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        key: `ModelField-DataModel-Shopify-SubscriptionContract-__gadget_graphql_subscription_contract_status`,
        initialStorageEpochKey: `FieldStorageEpoch-DataModel-Shopify-SubscriptionContract-__gadget_graphql_subscription_contract_status-initial`,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "status",
            shopifyFieldKey: "status",
            isShopifyEnum: true,
            availableInWebhook: ["subscription_contracts/create", "subscription_contracts/update"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: [{ name: "ACTIVE" }, { name: "CANCELLED" }, { name: "EXPIRED" }, { name: "FAILED" }, { name: "PAUSED" }],
        },
      },
    ],
  },
};

export const ShopifySubscriptionContract = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
